import {
  useState,
  useCallback,
  useRef,
  useEffect
} from 'react';
import { invalidate } from '@react-three/fiber';
import {
  ProjectDto,
  useAddSpoolsMutation,
  useRemoveSpoolsMutation
} from 'providers/api';
import { Model } from '../../hooks/useModelState';
import { PCNode } from '../../MineModel/Nodes';
import { createSpool, toSpoolImportDto } from '../utils/nodeCreation';
import { AlternativeNodeColour } from '../../MineModel/Nodes/PCNode';
import PCSpool from '../../MineModel/Spools/PCSpool';

/* --------- types --------- */

interface UseConnectNodesProps {
  project: ProjectDto;
  model: Model;
}

/* ================= CONNECT ================= */

export const useConnectNodes = ({ model, project }: UseConnectNodesProps) => {
  const [isSelectingNodes, setIsSelectingNodes] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState<PCNode[]>([]);
  const selectedNodesRef = useRef<PCNode[]>([]);
  const addSpoolsMutation = useAddSpoolsMutation(project.entityId);

  // The selection is mirrored in a ref so the pick handler can read it without a state updater:
  // StrictMode double-invokes updaters, and minting a spool inside one would send the connect twice.
  const setSelection = (nodes: PCNode[]) => {
    selectedNodesRef.current = nodes;
    setSelectedNodes(nodes);
  };

  // Connecting two nodes is exactly one new spool. The id is minted here so the optimistic spool and
  // the persisted one are the same entity; the server takes it verbatim.
  const submitConnection = (firstNode: PCNode, secondNode: PCNode) => {
    model.setNodeColour(firstNode.id, AlternativeNodeColour.DEFAULT);
    model.setNodeColour(secondNode.id, AlternativeNodeColour.DEFAULT);

    const spool = createSpool(firstNode, secondNode, model);
    spool.changeColour(model.resolveSpoolBaseColour(spool));
    model.addSpool(spool);

    addSpoolsMutation.mutate(
      { projectId: project.entityId, spools: [toSpoolImportDto(spool)] },
      { onError: () => model.removeSpools([spool]) },
    );
  };

  const handleNodeSelect = (node: PCNode | undefined) => {
    if (!model || !node) return;

    const prev = selectedNodesRef.current;

    if (prev.length === 0) {
      model.setNodeColour(node.id, AlternativeNodeColour.CONNECT);
      setSelection([node]);
      return;
    }

    if (prev[0].id === node.id) {
      model.setNodeColour(node.id, AlternativeNodeColour.DEFAULT);
      setSelection([]);
      return;
    }

    const firstNode = model.nodes[prev[0].id];
    const secondNode = model.nodes[node.id];
    setSelection([]);

    if (!firstNode || !secondNode) return;

    // Connectivity is now simply "a spool exists between them".
    if (model.findSpoolByPair(firstNode.id, secondNode.id)) {
      model.setNodeColour(firstNode.id, AlternativeNodeColour.DEFAULT);
      model.setNodeColour(secondNode.id, AlternativeNodeColour.DEFAULT);
      return;
    }

    submitConnection(firstNode, secondNode);
  };

  const handleSelectForConnectNode = () => {
    setIsSelectingNodes(true);
    setSelection([]);
  };

  const handleCancelSelection = () => {
    selectedNodesRef.current.forEach((node) => {
      model.setNodeColour(node.id, AlternativeNodeColour.DEFAULT);
    });
    setIsSelectingNodes(false);
    setSelection([]);
  };

  return {
    isSelectingNodes,
    selectedNodes,
    handleSelectForConnectNode,
    handleCancelSelection,
    handleNodeSelect,
  };
};

/* ================= DISCONNECT ================= */

export const useDisconnectNodes = ({ project, model }: UseConnectNodesProps) => {
  const [isSelectingSpool, setIsSelectingSpool] = useState(false);
  const isSelectingSpoolRef = useRef<boolean>(false);
  const selectedSpoolRef = useRef<PCSpool | null>(null);
  const removeSpoolsMutation = useRemoveSpoolsMutation(project.entityId);

  const restoreSpool = useCallback((m: Model) => {
    const spoolToRestore = selectedSpoolRef.current;
    if (!spoolToRestore) return;

    if (!m.spools[spoolToRestore.spoolId]) {
      m.addSpool(spoolToRestore);
    }

    selectedSpoolRef.current = null;
  }, []);

  // Disconnecting is exactly one spool removal — no node bookkeeping left to do on either endpoint.
  const handleSpoolSelect = useCallback((spool: PCSpool) => {
    if (!isSelectingSpoolRef.current || !model || !spool.pointA || !spool.pointB) return;

    selectedSpoolRef.current = spool;

    model.setNodeColour(spool.pointA.id, AlternativeNodeColour.DEFAULT);
    model.setNodeColour(spool.pointB.id, AlternativeNodeColour.DEFAULT);
    model.removeSpools([spool]);

    removeSpoolsMutation.mutate(
      { projectId: project.entityId, form: { spoolIds: [spool.spoolId], connections: [] } },
      {
        onSuccess: () => {
          selectedSpoolRef.current = null;
        },
        onError: () => restoreSpool(model),
      },
    );

    invalidate();
  }, [model, project, removeSpoolsMutation, restoreSpool]);

  const handleSelectForDisconnectNode = useCallback(() => {
    setIsSelectingSpool(true);
    isSelectingSpoolRef.current = true;
  }, []);

  const handleCancelSelection = useCallback(() => {
    setIsSelectingSpool(false);
    isSelectingSpoolRef.current = false;
  }, []);

  useEffect(() => () => {
    if (model && selectedSpoolRef.current) {
      restoreSpool(model);
    }
    invalidate();
  }, [restoreSpool]);

  return {
    isSelectingNodes: isSelectingSpool,
    selectedNodes: [],
    handleSelectForDisconnectNode,
    handleCancelSelection,
    handleSpoolSelect,
  };
};
